/**
 * CreativeFormulasSection — full creative formula analysis panel.
 *
 * Two halves:
 *   1. Dimension Breakdown — horizontal bar chart tabbed by Hook | Tone | Visual | CTA
 *      Each bar = one dimension value, width = estimated LTV value, colour = strength.
 *   2. Per-Persona Formula Leaderboard — the existing ranked formula table,
 *      now with richer metadata (LTV, CVR, visits).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Trophy, ChevronRight, Zap, TrendingUp, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchFormulaResponse,
  type WorkbenchFormulaBreakdownResponse,
  type WorkbenchFormulaBar,
  type WorkbenchFormulaDimension,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

interface Props {
  onDrillDown: (seed: ChatSeedInput) => void;
}

type Tab = { key: WorkbenchFormulaDimension; label: string };

const DIMENSION_TABS: Tab[] = [
  { key: 'hook_type', label: 'Hook' },
  { key: 'tone', label: 'Tone' },
  { key: 'visual_style', label: 'Visual' },
  { key: 'cta_style', label: 'CTA' },
];

const STRENGTH_COLORS: Record<string, string> = {
  strong: 'bg-teal-500',
  promising: 'bg-emerald-400',
  weak: 'bg-slate-300',
  unknown: 'bg-slate-200',
};

const STRENGTH_TEXT: Record<string, string> = {
  strong: 'text-teal-600',
  promising: 'text-emerald-600',
  weak: 'text-slate-400',
  unknown: 'text-slate-300',
};

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

// ─── Dimension Breakdown panel ────────────────────────────────────────────────

function DimensionBreakdown({ onDrillDown }: { onDrillDown: (s: ChatSeedInput) => void }) {
  const { clientUid, personaId } = useCreativeWorkbench();
  const [activeDim, setActiveDim] = useState<WorkbenchFormulaDimension>('hook_type');
  const [data, setData] = useState<WorkbenchFormulaBreakdownResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFormulaBreakdown({ clientUid, dimension: activeDim, personaId })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid, personaId, activeDim]);

  const bars = data?.bars ?? [];
  const winner = data?.winner;
  const topBar = bars.find((b) => b.label === winner);

  return (
    <div className="space-y-3">
      {/* Dimension tabs */}
      <div className="flex items-center gap-1 border-b border-slate-100 pb-2">
        {DIMENSION_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveDim(tab.key)}
            className={clsx(
              'px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors',
              activeDim === tab.key
                ? 'bg-teal-50 text-teal-700 ring-1 ring-teal-200'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50',
            )}
          >
            {tab.label}
          </button>
        ))}
        <span className="ml-auto text-[10px] font-mono text-slate-400">
          LTV value · strength
        </span>
      </div>

      {/* Winner callout */}
      {topBar && !loading && (
        <div className="flex items-center gap-2 bg-teal-50 border border-teal-100 rounded-lg px-3 py-2">
          <Trophy size={13} className="text-teal-500 shrink-0" />
          <div className="min-w-0">
            <span className="text-[11px] font-bold text-teal-800 capitalize">
              {topBar.label}
            </span>
            <span className="text-[11px] text-teal-600 ml-1">
              wins this dimension —{' '}
              {CURRENCY.format(topBar.estimated_value)} est. LTV value,{' '}
              {topBar.ltv_roas.toFixed(2)}x LTV-ROAS,{' '}
              {topBar.ctr.toFixed(2)}% CTR
            </span>
          </div>
        </div>
      )}

      {/* Bar chart */}
      {loading && !data && (
        <div className="text-xs text-slate-400 py-4 text-center">Loading...</div>
      )}
      <div className="space-y-1.5">
        {bars.map((bar) => (
          <BarRow
            key={bar.label}
            bar={bar}
            dimension={activeDim}
            onDrillDown={onDrillDown}
          />
        ))}
      </div>
    </div>
  );
}

function BarRow({
  bar,
  dimension,
  onDrillDown,
}: {
  bar: WorkbenchFormulaBar;
  dimension: WorkbenchFormulaDimension;
  onDrillDown: (s: ChatSeedInput) => void;
}) {
  const dimLabel = DIMENSION_TABS.find((t) => t.key === dimension)?.label ?? dimension;
  return (
    <div className="group">
      <div className="flex items-center gap-2 mb-0.5">
        {/* Label + drill */}
        <button
          onClick={() =>
            onDrillDown({
              question: `Why does the "${bar.label}" ${dimLabel.toLowerCase()} perform the way it does?`,
              chart_axis: { series: dimension, dimension: 'formula', formula: bar.label },
            })
          }
          className="text-[11px] font-semibold text-slate-700 hover:text-teal-600 capitalize w-36 shrink-0 text-left truncate"
          title={bar.label}
        >
          {bar.label}
        </button>

        {/* Bar track */}
        <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden relative">
          <div
            className={clsx(
              'h-full rounded-full transition-all duration-500',
              STRENGTH_COLORS[bar.strength] ?? 'bg-slate-300',
            )}
            style={{ width: `${Math.max(bar.bar_width_pct, 2)}%` }}
          />
        </div>

        {/* Metrics */}
        <div className="flex items-center gap-3 text-right shrink-0 w-48">
          <span className="text-[10px] font-mono text-slate-500 w-12 text-right">
            {bar.visitors.toLocaleString()} vis
          </span>
          <span className="text-[10px] font-mono text-slate-500 w-10 text-right">
            {bar.ctr.toFixed(1)}% CTR
          </span>
          <span
            className={clsx(
              'text-[11px] font-bold w-16 text-right',
              STRENGTH_TEXT[bar.strength] ?? 'text-slate-400',
            )}
          >
            {bar.estimated_value > 0 ? CURRENCY.format(bar.estimated_value) : `$${bar.spend.toFixed(0)}`}
          </span>
        </div>
      </div>

      {/* Sub-stats on hover */}
      <div className="hidden group-hover:flex items-center gap-4 pl-[9.5rem] text-[10px] text-slate-400 mb-1">
        <span>{bar.ad_count} ads</span>
        <span>{CURRENCY.format(bar.spend)} spend</span>
        <span>{bar.ltv_roas.toFixed(2)}x LTV-ROAS</span>
        <span>{bar.spend_share_pct}% of spend</span>
      </div>
    </div>
  );
}

// ─── Per-Persona Formula Leaderboard ─────────────────────────────────────────

function PersonaFormulaLeaderboard({ onDrillDown }: { onDrillDown: (s: ChatSeedInput) => void }) {
  const { clientUid, personaId } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchFormulaResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [expandedPersonas, setExpandedPersonas] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getFormulaAffinity({ clientUid, personaId, limit: 6 })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientUid, personaId]);

  const togglePersona = (name: string) => {
    setExpandedPersonas((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (loading && !data) {
    return <div className="text-xs text-slate-400">Loading...</div>;
  }
  if (!data || data.personas.length === 0) {
    return <div className="text-xs text-slate-400">No formula attribution yet.</div>;
  }

  return (
    <div className="space-y-1">
      {data.personas.map((p) => {
        const expanded = expandedPersonas.has(p.persona_name);
        const topFormula = p.formulas[0];
        return (
          <div key={p.persona_name} className="border border-slate-100 rounded-lg overflow-hidden">
            {/* Persona header — click to expand */}
            <button
              onClick={() => togglePersona(p.persona_name)}
              className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Zap size={11} className="text-teal-500 shrink-0" />
                <span className="text-[11px] font-bold text-navy-900 truncate">
                  {p.persona_name}
                </span>
                {topFormula && (
                  <span className="text-[10px] text-slate-500 font-mono truncate hidden sm:block">
                    best: {topFormula.formula}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-[10px] font-mono text-slate-400">
                  {p.formulas.length} formula{p.formulas.length !== 1 ? 's' : ''}
                </span>
                {topFormula && (
                  <span className="text-[11px] font-bold text-teal-600">
                    {CURRENCY.format(topFormula.estimated_value)}
                  </span>
                )}
                <ChevronRight
                  size={12}
                  className={clsx(
                    'text-slate-400 transition-transform',
                    expanded && 'rotate-90',
                  )}
                />
              </div>
            </button>

            {/* Expanded formula rows */}
            {expanded && (
              <div className="divide-y divide-slate-50">
                {p.formulas.map((f, idx) => (
                  <div
                    key={f.formula}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-teal-50/40 transition-colors group"
                  >
                    {/* Rank badge */}
                    <span
                      className={clsx(
                        'text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center shrink-0',
                        idx === 0
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-slate-100 text-slate-500',
                      )}
                    >
                      {idx + 1}
                    </span>

                    {/* Formula pills */}
                    <button
                      onClick={() =>
                        onDrillDown({
                          question: `Why does ${p.persona_name} respond to "${f.formula}" creative?`,
                          chart_axis: {
                            series: 'formula',
                            dimension: 'persona',
                            formula: f.formula,
                            persona: p.persona_name,
                          },
                        })
                      }
                      className="flex-1 min-w-0 flex items-center gap-1 text-left group-hover:text-teal-600"
                    >
                      {f.formula.split(' · ').map((part, i) => (
                        <span
                          key={i}
                          className="text-[10px] bg-slate-100 group-hover:bg-teal-100 text-slate-600 group-hover:text-teal-700 rounded px-1.5 py-0.5 font-mono capitalize transition-colors"
                        >
                          {part}
                        </span>
                      ))}
                    </button>

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-right shrink-0">
                      <span className="text-[10px] font-mono text-slate-400">
                        {f.visitors.toLocaleString()} vis
                      </span>
                      <span className="text-[11px] font-bold text-teal-600 w-14 text-right">
                        {f.estimated_value > 0
                          ? CURRENCY.format(f.estimated_value)
                          : '—'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Root section ─────────────────────────────────────────────────────────────

export function CreativeFormulasSection({ onDrillDown }: Props) {
  const navigate = useNavigate();
  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-5">
      {/* Section header */}
      <header className="flex items-center justify-between">
        <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
          <Layers size={14} className="text-teal-500" />
          Creative Formula Analysis
        </h2>
        <button
          onClick={() => navigate('/creative/performance/formulas')}
          className="flex items-center gap-1 text-[11px] text-teal-600 hover:text-teal-700 font-semibold"
        >
          Full analysis
          <ArrowRight size={11} />
        </button>
      </header>

      {/* Part 1: Dimension bar chart */}
      <div>
        <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Dimension Breakdown
        </h3>
        <DimensionBreakdown onDrillDown={onDrillDown} />
      </div>

      {/* Divider */}
      <div className="border-t border-slate-100" />

      {/* Part 2: Per-persona leaderboard */}
      <div>
        <h3 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Top Formulas by Persona
        </h3>
        <PersonaFormulaLeaderboard onDrillDown={onDrillDown} />
      </div>
    </section>
  );
}
